import type {Emphasis, MessageToken, Tone, TokenRole} from "./types";

/**
 * Where a token's appearance is decided.
 *
 * The server sends no markup and no colours -- it says what a value IS and what it MEANS, and this
 * file turns that into Tailwind classes. Keeping the mapping in one place is what makes every
 * message in the product read consistently, and what makes a restyle an edit here rather than a
 * sweep through hundreds of server-side emit sites.
 */

/** Fallbacks when a token does not override its tone. Mirrors `TokenRole.getDefaultTone()`. */
const roleDefaultTone: Record<string, Tone> = {
  SUBJECT: "NEUTRAL",
  EXPECTED: "POSITIVE",
  ACTUAL: "NEGATIVE",
  DELTA: "CAUTION",
  THRESHOLD: "CAUTION",
  LOCATION: "MUTED",
  SOURCE: "MUTED",
  COUNT: "NEUTRAL",
  REMEDY: "POSITIVE",
  CONTEXT: "NEUTRAL"
};

/**
 * Tone to colour, using the brand tokens shared with oilfieldtrackingservices.com.
 *
 * NEUTRAL is brand ink rather than a neutral grey, and NEGATIVE is the OTS red rather than
 * Tailwind's, so a diagnostic reads as part of the product instead of as a generic web page.
 */
const toneClasses: Record<Tone, string> = {
  NEUTRAL: "text-brand-900 dark:text-brand-100",
  POSITIVE: "text-green-700 dark:text-green-400",
  CAUTION: "text-amber-700 dark:text-amber-400",
  NEGATIVE: "text-ots-red-700 dark:text-ots-red-400",
  MUTED: "text-brand-600 dark:text-brand-300 font-normal"
};

/** Presentation mechanics per value type: monospacing, tabular figures, no mid-value wrapping. */
const typeClasses: Record<string, string> = {
  ID: "font-mono text-[0.95em] whitespace-nowrap",
  CODE: "font-mono text-[0.95em] whitespace-nowrap",
  URL: "font-mono text-[0.95em] whitespace-nowrap",
  NUMBER: "tabular-nums whitespace-nowrap",
  QUANTITY: "tabular-nums whitespace-nowrap",
  MONEY: "tabular-nums whitespace-nowrap",
  PERCENT: "tabular-nums whitespace-nowrap",
  DURATION: "tabular-nums whitespace-nowrap",
  DATE: "whitespace-nowrap",
  TIME: "whitespace-nowrap",
  DATETIME: "whitespace-nowrap",
  // A workflow state reads as a chip. The ring and ground are neutral so the tone still carries the
  // meaning -- an EXPECTED status stays green and an ACTUAL one red, exactly as they do in prose.
  STATUS: "rounded-md bg-brand-50 px-1.5 py-0.5 ring-1 ring-brand-200 whitespace-nowrap dark:bg-brand-900 dark:ring-brand-700"
};

/** A couple of roles carry weight beyond colour. */
const roleClasses: Record<string, string> = {
  SUBJECT: "font-semibold",
  THRESHOLD: "italic"
};

const emphasisClasses: Record<string, string> = {
  BOLD: "font-bold",
  ITALIC: "italic",
  UNDERLINE: "underline",
  STRIKETHROUGH: "line-through",
  MONOSPACE: "font-mono",
  HIGHLIGHT: "bg-amber-100 dark:bg-amber-900/40 rounded px-1",
  SUBTLE: "text-brand-600 dark:text-brand-300 font-normal"
};

export const effectiveTone = (token: MessageToken): Tone => {
  if (token.tone && token.tone in toneClasses) {
    return token.tone as Tone;
  }
  return roleDefaultTone[token.role ?? ""] ?? "NEUTRAL";
};

/**
 * The full class string for a token.
 *
 * Order matters: type first (mechanics), then role, then tone, then emphasis -- so an explicit
 * emphasis from the emitter wins over what its role would have implied.
 */
export const tokenClasses = (token: MessageToken, clickable: boolean): string => {
  const classes = ["font-medium"];

  const type = token.type ?? "";
  if (typeClasses[type]) {
    classes.push(typeClasses[type]);
  }

  const role = token.role ?? "";
  if (roleClasses[role]) {
    classes.push(roleClasses[role]);
  }

  classes.push(toneClasses[effectiveTone(token)]);

  for (const emphasis of token.emphasis ?? []) {
    const emphasisClass = emphasisClasses[emphasis as Emphasis];
    if (emphasisClass) {
      classes.push(emphasisClass);
    }
  }

  if (clickable) {
    classes.push("cursor-pointer underline decoration-dotted underline-offset-2 hover:decoration-solid");
  }

  return classes.join(" ");
};

export type {TokenRole};
